<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MRentalGeneralDataEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    rental: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const formId = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'md',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId },
    ux: {},
    dataRefs: {
      formId,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.pgettext('Rental', 'Edit General Information') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.rental,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rental');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_91rhnbuk7_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_2238mkvm7_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_bx42618un_getActionArgs = async ($event) => {
    return {
      objectType: 'rental',
      op: 'upsert',
      data: props.rental,
    };
  };
  const act_2434514588044777_closeComponent_cwdsn8ozv_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_91rhnbuk7 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_91rhnbuk7_getActionArgs,
    });
  const inst_evh_7315092382398562_close_wgdo5p7yu =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_91rhnbuk7,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_wgdo5p7yu],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_rnz8d4s68 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_35k0iy0m7 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_rnz8d4s68,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_35k0iy0m7],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_l23t1fknc =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_5qadbz9my =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_l23t1fknc,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_5qadbz9my],
    event: 'close',
  });
  const act_1040167445267876_closeModal_2238mkvm7 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_2238mkvm7_getActionArgs,
    });
  const inst_evh_1040167445267876_success_z5b1bswui =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_2238mkvm7,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_bx42618un = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_bx42618un_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_z5b1bswui],
  });
  const inst_evh_1040167445267876_submit_coymc1k2c =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_bx42618un,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_coymc1k2c],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_cwdsn8ozv =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2434514588044777_closeComponent_cwdsn8ozv_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_dvtniuny1 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_cwdsn8ozv,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_dvtniuny1],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_wgdo5p7yu,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_35k0iy0m7,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_5qadbz9my,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_z5b1bswui,
    inst_evh_1040167445267876_submit_coymc1k2c,
    save: inst_evh_1040167445267876_submit_coymc1k2c,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_dvtniuny1,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_wgdo5p7yu = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_wgdo5p7yu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_35k0iy0m7 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_35k0iy0m7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_5qadbz9my = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_5qadbz9my.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_z5b1bswui = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_z5b1bswui.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_coymc1k2c = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_coymc1k2c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_dvtniuny1 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_dvtniuny1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="MRentalGeneralDataEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="rental"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <FieldsetRentalGeneralData :rental="rental" :formId="formId">
          </FieldsetRentalGeneralData>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save changes')"
          :disabled="!forms.rental || !forms.rental.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
